function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
import { ContextPanelReactProps } from 'common/widgets/context-panel';
import { EntityType, SharableEntityType } from 'common/report/util/saved-entities/types';
import { promptSharing } from 'common/widgets/sharing';
import { getProjectId, getReportUrl, getWorkspaceId } from 'common/report/util';
import { showMetricsApiErrorToast, updateMetric } from 'common/report/queries/app-api/metrics';
import { ActionType, ContextPanelData, DetailType } from 'common/widgets/context-panel/types';
import { MetricsResource } from 'common/report/model-resources/metrics';
import { ModelStatus } from 'common/util/model-resource/model-resource';
import { getEditCapability, getShareCapability } from 'common/report/util/saved-entities';
import { MetricsResultsMap } from 'app_api/projects/metrics/types';
import type { ContextPanelRendererParams } from '../child-menu';

/**
 * Provides handlers for events inside a metric context panel
 */
export const createContextPanelEventHandlers = function createContextPanelEventHandlers(): Partial<ContextPanelReactProps> {
  let params: Partial<ContextPanelRendererParams> = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  // handles actions from, the context panel
  const handleContextPanelAction = async (ev: CustomEvent) => {
    const action = ev.detail.name;
    const metricId = ev.detail.item.id;
    if (action === ActionType.ShareFormula) {
      await promptSharing({
        type: SharableEntityType.Metric,
        id: metricId,
        'project-id': Number(getProjectId())
      });
    } else if (ev.detail.item.url && action === ActionType.ViewEntity) {
      window.open(ev.detail.item.url, "_blank");
    } else {
      throw new Error("action handler not implemented");
    }
  };
  const handleContextPanelEditDescription = async (_ref: CustomEvent) => {
    let {
      detail
    }: CustomEvent = _ref;
    const metricId = detail.item.id;
    const updatedDescription = detail.description;
    let results: MetricsResultsMap;
    try {
      results = await updateMetric({
        metricId,
        metric: {
          description: updatedDescription
        }
      });
    } catch (_unused) {
      results = null;
      showMetricsApiErrorToast();
    }
    if (results) {
      params.availableContexts.metrics.update(results, {
        partialUpdate: false
      });
    }
  };
  return {
    onHoverIn: params.handleHoverIn,
    onHoverOut: params.handleHoverOut,
    onTooltipContextAction: handleContextPanelAction,
    onTooltipContextEditDescription: handleContextPanelEditDescription
  };
};

/**
 * Generates the context panel data for Metrics to allow for sharing and description editing
 */
export function generateFormulaContextPanelData(_ref2: {
  id: number;
  metricsContext: MetricsResource;
  canWriteMetrics?: boolean;
  canShare?: boolean;
}): ContextPanelData {
  var _metric$last_verified, _metric$last_verified2;
  let {
    id,
    metricsContext,
    canWriteMetrics = false,
    canShare = false
  }: {
    id: number;
    metricsContext: MetricsResource;
    canWriteMetrics?: boolean;
    canShare?: boolean;
  } = _ref2;
  const model = metricsContext.getOne(id);
  const metric = model.value;
  if (model.status === ModelStatus.Loading || !metric) {
    return null;
  }
  const permissions = getEditCapability(metric);
  const actions: ContextPanelData[`actions`] = [];
  const links: ContextPanelData[`links`] = [];
  const details: ContextPanelData[`details`] = [];

  // manage sharing and permissions action
  if (canShare) {
    actions.push({
      name: ActionType.ShareFormula,
      label: _ref3 => {
        let {
          t
        } = _ref3;
        return t("query-builder.context-menu.share");
      },
      icon: "invite",
      // Only superadmin or editor can share
      disabled: !getShareCapability(metric, EntityType.Metric).enableShare,
      disabledTooltipText: _ref4 => {
        let {
          t
        } = _ref4;
        return t("query-builder.context-menu.share-no-access");
      },
      item: {
        id: metric.id,
        superadminOverrideShare: getShareCapability(metric).requireSuperadminOverride
      }
    });
  }

  // view entity in lexicon link
  links.push({
    icon: "share",
    href: getReportUrl("lexicon", {
      projectId: getProjectId(),
      workspaceId: getWorkspaceId()
    }) + "/transformations/formulas/".concat(metric.id),
    tooltipText: _ref5 => {
      let {
        t
      } = _ref5;
      return t("query-builder.context-menu.view-in-lexicon");
    }
  });
  actions.push({
    name: ActionType.ViewEntity,
    label: _ref6 => {
      let {
        t
      } = _ref6;
      return t("query-builder.context-menu.view-in-lexicon");
    },
    icon: "share",
    item: {
      id: metric.id,
      name: metric.name,
      url: getReportUrl("lexicon", {
        projectId: getProjectId(),
        workspaceId: getWorkspaceId()
      }) + "/transformations/formulas/".concat(metric.id)
    }
  });
  if (metric.created_by) {
    details.push({
      name: _ref7 => {
        let {
          t
        } = _ref7;
        return t("query-builder.context-menu.creator");
      },
      type: DetailType.User,
      value: _objectSpread({}, metric.created_by)
    });
  }
  return {
    actions,
    links,
    header: metric.name,
    subHeader: _ref8 => {
      let {
        t
      } = _ref8;
      return t("query-builder.metrics.formula");
    },
    entityIcon: "formula-scroll",
    description: metric.description,
    entityType: EntityType.Metric,
    excludedMetricIcons: ["pie", "pulse"],
    details,
    // allows for editing the description of the metric
    editItem: canWriteMetrics && permissions.enableEdit ? {
      id: metric.id,
      type: EntityType.Metric,
      name: metric.name
    } : null,
    verified: metric.verified,
    verifiedAt: metric.last_verified,
    verifiedBy: ((_metric$last_verified = metric.last_verified_by) === null || _metric$last_verified === void 0 ? void 0 : _metric$last_verified.name) || ((_metric$last_verified2 = metric.last_verified_by) === null || _metric$last_verified2 === void 0 ? void 0 : _metric$last_verified2.email)
  };
}